import * as rd from '@devexperts/remote-data-ts'
import { isSpotSubType } from '@spiaggeit/spit-core'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounceCallback } from 'usehooks-ts'

import { CartFooter } from '@/components/CartFooter'
import { GridWithRecap } from '@/components/GridWithRecap'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { PageSection } from '@/components/PageSection'
import { ReservationRecapCard } from '@/components/ReservationRecapCard'
import { INSERT_DATA_PATH } from '@/app/router/paths.ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { useAppPath } from '@/hooks/useAppPath.ts'

import { useFetchPricePreview } from '@/hooks/useFetchPricePreview.ts'
import { usePrevious } from '@/hooks/usePrevious'
import { useSyncRestoredCartItems } from '@/hooks/useRestorePendingCart'
import { FeaturedService, ServiceInfoPurchaseType } from '@/models/service'
import { appSlice, isKioskSelector } from '@/store/appSlice'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'

import { cartSlice } from '@/store/cartSlice'
import {
  availableFeaturedServicesSelector,
  formattedServicesSelector,
} from '@/store/cartSlice/selectors/selectAvailableFeaturedServices.ts'
import { extraSlice } from '@/store/extraSlice.ts'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { servicesSlice } from '@/store/servicesSlice'

import { NO, YES } from '@/utils/constants'
import { sumReservationsSeatsAmount } from '@/utils/getReservationSeatsAmount'
import { getServiceMinimumQuantity } from '@/utils/getServiceMinimumQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'

import {
  getBoughtServicesTotal,
  getDaysFromPriceMode,
} from '@/utils/timeSchedules'
import weatherInsuranceImage from '../../images/weather-insurance.png'
import { CardExtra } from './CardExtra'
import { WeatherCard } from './WeatherCard'

export function InsertInsurance() {
  const { t } = useTranslation()
  const isKiosk = useAppSelector(isKioskSelector)
  const isPool = useAppSelector(appSlice.selectors.isPool)
  const weatherInsurancePrice = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )
  const isWeatherInsuranceInitiallyVisible = useAppSelector(
    extraSlice.selectors.isWeatherInsuranceInitiallyVisible
  )
  const previousIsWeatherInsuranceInitiallyVisible = usePrevious(
    isWeatherInsuranceInitiallyVisible
  )
  const license = useAppSelector(licenseSlice.selectors.license)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useFetchPricePreview()
  // Keep cartItems in sync with restored cartReservations so the cart preview /
  // drawer stays consistent with the persisted total on this route too.
  useSyncRestoredCartItems()

  const services = useAppSelector(servicesSlice.selectors.data)
  const bookingType = useAppSelector(cartSlice.selectors.selectedBookingType)
  const isWeatherInsuranceAddedToCart = useAppSelector(
    cartSlice.selectors.isWeatherInsuranceAddedToCart
  )

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const featuredServices = useAppSelector(
    servicesSlice.selectors.featuredServices
  )
  const selectedBookingType = useAppSelector(
    cartSlice.selectors.selectedBookingType
  )
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const insertDataPath = useAppPath(INSERT_DATA_PATH)

  const purchaseType =
    bookingType === BookingAvailability.TICKETS
      ? ServiceInfoPurchaseType.ALONE
      : ServiceInfoPurchaseType.WITH_SPOT

  const featuredExtraServices = useAppSelector(
    availableFeaturedServicesSelector
  )
  const formattedServices = useAppSelector(formattedServicesSelector)
  const hasToRedirectToNextPage =
    isWeatherInsuranceInitiallyVisible !==
      previousIsWeatherInsuranceInitiallyVisible &&
    previousIsWeatherInsuranceInitiallyVisible &&
    !isWeatherInsuranceInitiallyVisible &&
    featuredExtraServices &&
    featuredExtraServices.length === 0

  const dispatch = useAppDispatch()

  const quoteCallback = useCallback(() => {
    dispatch(
      cartSlice.actions.quote({
        isQuoteForTickets: selectedBookingType === BookingAvailability.TICKETS,
        needsToUpdateCartTotal: true,
      })
    )
  }, [dispatch])

  const quote = useDebounceCallback(quoteCallback, 500)

  const handleUpdateCartWeather = (isWeatherInsuranceSelected: boolean) => {
    dispatch(
      cartSlice.actions.setWeatherInsurance(
        isWeatherInsuranceSelected ? 'yes' : 'no'
      )
    )

    if (!rd.isSuccess(weatherInsurancePrice)) return
    if (rd.isSuccess(weatherInsurancePrice)) {
      window.dataLayer.push({
        Stabilimento: license?.beach.name ?? license?.license,
        currency: 'EUR',
        event: isWeatherInsuranceSelected
          ? 'add_weather_insurance'
          : 'remove_weather_insurance',
        value: weatherInsurancePrice.value,
      })
    }
  }

  function handleUpdateCartProductQuantity(featuredService: FeaturedService) {
    return (selectedQuantity: number) => {
      switch (featuredService.scheduleType) {
        case 'daily': {
          const days = getDaysFromPriceMode(featuredService, dayDifference)
          if (days.length === 0) {
            return
          }

          dispatch(
            cartSlice.actions.addFeaturedServiceToCart({
              days,
              featuredService,
              formattedServices,
              quantity: selectedQuantity,
              timeSchedule: featuredService.timeSchedules[0],
            })
          )
        }
      }
      quote()
    }
  }

  const sections = [
    ...(featuredExtraServices?.length
      ? [
          {
            content: (
              <>
                {featuredExtraServices.map((featuredService) => {
                  const compatibleReservations = cartReservations.filter(
                    (res) =>
                      isSpotSubType(res.spotType) &&
                      featuredService.sectors.includes(res.sectorId)
                  )

                  const spotsAmount = compatibleReservations.length
                  const seatsAmount = sumReservationsSeatsAmount(
                    compatibleReservations
                  )

                  const maxQuantity = getServiceSingleLimit({
                    dayDifference,
                    seatsAmount,
                    service: featuredService,
                    spotsAmount,
                  })
                  const minQuantity = getServiceMinimumQuantity(
                    featuredService,
                    seatsAmount,
                    dayDifference,
                    spotsAmount
                  )

                  const initialQuantity = compatibleReservations.reduce(
                    (sum, reservation) =>
                      sum +
                      getBoughtServicesTotal(
                        reservation.boughtServices[featuredService.serviceId]
                      ),
                    0
                  )

                  return (
                    <CardExtra
                      initialQuantity={initialQuantity}
                      key={featuredService.serviceId}
                      maxQuantity={maxQuantity}
                      minQuantity={minQuantity}
                      onQuantityChange={handleUpdateCartProductQuantity(
                        featuredService
                      )}
                      service={featuredService}
                    />
                  )
                })}
              </>
            ),
            title: t('extra.featured.title'),
          },
        ]
      : []),
    ...(!isKiosk && isWeatherInsuranceInitiallyVisible
      ? [
          {
            content: (
              <WeatherCard
                description={t('extra.weatherInsurance.description', {
                  context: isPool ? 'pool' : 'beach',
                })}
                features={[
                  {
                    copy: t('extra.weatherInsurance.firstListItem'),
                    icon: 'Business',
                  },
                  {
                    copy: t('extra.weatherInsurance.secondListItem'),
                    icon: 'Weather',
                  },
                  {
                    copy: t('extra.weatherInsurance.thirdListItem'),
                    icon: 'Communication',
                  },
                ]}
                image={{ alt: '', src: weatherInsuranceImage }}
                insuranceProvider={{
                  image: { src: '/images/logo-revo.svg' },
                  name: 'REVO Insurance S.p.A',
                }}
                isInitiallySelected={isWeatherInsuranceAddedToCart}
                isPending={rd.isPending(weatherInsurancePrice)}
                onSelectChange={handleUpdateCartWeather}
                price={
                  rd.isSuccess(weatherInsurancePrice)
                    ? weatherInsurancePrice.value
                    : undefined
                }
                title={t('extra.weatherInsurance.title')}
              />
            ),
            title: t('extra.protectYourself.title'),
          },
        ]
      : []),
  ]

  useEffect(() => {
    if (!services) {
      dispatch(servicesSlice.actions.load({ purchaseType }))
    }
  }, [services, dispatch, purchaseType])

  const hasPushedDataLayer = useRef(false)

  useEffect(() => {
    if (!hasPushedDataLayer.current && rd.isSuccess(weatherInsurancePrice)) {
      window.dataLayer.push({
        'Assicurazione meteo disponibile':
          weatherInsurancePrice.value > 0 ? YES : NO,
      })
      hasPushedDataLayer.current = true
    }
  }, [weatherInsurancePrice])

  useEffect(() => {
    if (!rd.isSuccess(featuredServices)) {
      dispatch(servicesSlice.actions.fetchFeaturedServices())
    }
  }, [featuredServices])

  useEffect(() => {
    dispatch(cartSlice.actions.setSelectedElementReservation(null))
  }, [dispatch])

  useEffect(() => {
    if (hasToRedirectToNextPage) {
      navigate({
        pathname: insertDataPath,
        search: searchParams.toString(),
      })
    }
  }, [hasToRedirectToNextPage])

  if (hasToRedirectToNextPage) {
    return <LoaderTrigger />
  }
  const nextPath = insertDataPath

  return (
    <>
      <main className="container grow py-4 lg:py-8">
        <GridWithRecap className="mb-8 lg:mb-8 lg:mt-0">
          <GridWithRecap.ContentContainer>
            <h1 className="sr-only">Extra</h1>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <PageSection key={index} title={section.title}>
                  {section.content}
                </PageSection>
              ))}
            </div>
          </GridWithRecap.ContentContainer>

          <GridWithRecap.RecapContainer className="hidden lg:flex">
            <ReservationRecapCard nextPath={nextPath} />
          </GridWithRecap.RecapContainer>
        </GridWithRecap>
      </main>

      <CartFooter className="lg:hidden" nextPath={nextPath} />
    </>
  )
}
